/**
 * React Query hooks for SurviveIt API
 * Provides queries and mutations for all backend endpoints
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  authAPI,
  userAPI,
  moduleAPI,
  lessonAPI,
  tasksAPI,
  badgeAPI,
  simulationAPI,
  notificationAPI,
  exportAPI,
  offlineAPI,
  intelligenceAPI,
  emergencyAPI,
  onboardingAPI,
  utils
} from '../utils/api'
import toast from 'react-hot-toast'

// ============ QUERY HOOKS ============

// Modules
export const useModules = () => {
  return useQuery({
    queryKey: ['modules'],
    queryFn: moduleAPI.getAll,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const useModule = (moduleId) => {
  return useQuery({
    queryKey: ['modules', moduleId],
    queryFn: () => moduleAPI.getById(moduleId),
    enabled: !!moduleId,
  })
}

// User
export const useUserProfile = () => {
  return useQuery({
    queryKey: ['user', 'profile'],
    queryFn: userAPI.getProfile,
  })
}

export const useUserProgress = () => {
  return useQuery({
    queryKey: ['user', 'progress'],
    queryFn: userAPI.getProgress,
  })
}

export const useUserAnalytics = () => {
  return useQuery({
    queryKey: ['user', 'analytics'],
    queryFn: userAPI.getAnalytics,
  })
}

// Daily Tasks
export const useDailyTasks = () => {
  return useQuery({
    queryKey: ['tasks', 'daily'],
    queryFn: tasksAPI.getDailyTasks,
  })
}

export const useTaskHistory = () => {
  return useQuery({
    queryKey: ['tasks', 'history'],
    queryFn: tasksAPI.getTaskHistory,
  })
}

// Badges
export const useUserBadges = () => {
  return useQuery({
    queryKey: ['badges', 'user'],
    queryFn: badgeAPI.getUserBadges,
  })
}

export const useAllBadges = () => {
  return useQuery({
    queryKey: ['badges', 'all'],
    queryFn: badgeAPI.getAllBadges,
  })
}

// Simulations
export const useSimulations = () => {
  return useQuery({
    queryKey: ['simulations'],
    queryFn: simulationAPI.getAll,
  })
}

export const useSimulation = (simulationId) => {
  return useQuery({
    queryKey: ['simulations', simulationId],
    queryFn: () => simulationAPI.getById(simulationId),
    enabled: !!simulationId,
  })
}

export const useSimulationHistory = () => {
  return useQuery({
    queryKey: ['simulations', 'history'],
    queryFn: simulationAPI.getHistory,
  })
}

// Notifications
export const useNotificationPreferences = () => {
  return useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: notificationAPI.getPreferences,
  })
}

export const useNotificationSubscriptions = () => {
  return useQuery({
    queryKey: ['notifications', 'subscriptions'],
    queryFn: notificationAPI.getSubscriptions,
  })
}

// Intelligence
export const useIntelligenceStatus = () => {
  return useQuery({
    queryKey: ['intelligence', 'status'],
    queryFn: intelligenceAPI.getStatus,
  })
}

// Export
export const useExportStats = () => {
  return useQuery({
    queryKey: ['export', 'stats'],
    queryFn: exportAPI.getStats,
  })
}

// Family
export const useFamily = () => {
  return useQuery({
    queryKey: ['user', 'family'],
    queryFn: userAPI.getFamily,
  })
}

// ============ MUTATION HOOKS ============

// Auth Mutations
export const useLogin = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: authAPI.login,
    onSuccess: (data) => {
      queryClient.setQueryData(['user', 'profile'], data.user)
      toast.success('Welcome back!')
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

export const useRegister = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: authAPI.register,
    onSuccess: (data) => {
      queryClient.setQueryData(['user', 'profile'], data.user)
      toast.success('Account created successfully!')
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: authAPI.forgotPassword,
    onSuccess: () => {
      toast.success('Password reset email sent!')
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

export const useResetPassword = () => {
  return useMutation({
    mutationFn: ({ token, newPassword }) => authAPI.resetPassword(token, newPassword),
    onSuccess: () => {
      toast.success('Password reset successfully!')
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

// User Mutations
export const useUpdateProfile = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: userAPI.updateProfile,
    onSuccess: (data) => {
      queryClient.setQueryData(['user', 'profile'], data.user)
      queryClient.invalidateQueries({ queryKey: ['user'] })
      toast.success('Profile updated!')
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

// Lesson Mutations
export const useCompleteLesson = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ lessonId, score, timeSpent }) =>
      lessonAPI.completeLesson(lessonId, { score, timeSpent }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user', 'progress'] })
      queryClient.invalidateQueries({ queryKey: ['modules'] })
      queryClient.invalidateQueries({ queryKey: ['badges'] })

      if (data.xpGained > 0) {
        toast.success(`+${data.xpGained} XP earned!`, {
          icon: '⭐',
          duration: 3000
        })
      }

      if (data.leveledUp) {
        toast.success(`Level Up! You're now Level ${data.level}!`, {
          icon: '🎉',
          duration: 5000
        })
      }

      if (data.newBadges && data.newBadges.length > 0) {
        data.newBadges.forEach(badge => {
          toast.success(`New Badge: ${badge.name}!`, {
            icon: '🏆',
            duration: 5000
          })
        })
      }
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

export const useSubmitQuiz = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ lessonId, answers, timeSpent }) =>
      lessonAPI.submitQuiz(lessonId, { answers, timeSpent }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user', 'progress'] })
      queryClient.invalidateQueries({ queryKey: ['modules'] })

      if (data.passed) {
        toast.success(`Quiz passed! Score: ${data.score}%`, {
          icon: '✅',
          duration: 4000
        })
      } else {
        toast.error(`Score: ${data.score}%. Keep trying!`, {
          icon: '📝',
          duration: 4000
        })
      }
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

// Daily Tasks Mutations
export const useCompleteTask = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: tasksAPI.completeTask,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['user', 'progress'] })

      if (data.xpGained > 0) {
        toast.success(`Task completed! +${data.xpGained} XP`, {
          icon: '✓',
          duration: 3000
        })
      }
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

// Simulation Mutations
export const useStartSimulation = () => {
  return useMutation({
    mutationFn: simulationAPI.start,
    onSuccess: () => {
      toast.success('Simulation started!', { icon: '🎮' })
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

export const useSubmitSimulationDecision = () => {
  return useMutation({
    mutationFn: ({ simulationId, sessionId, choice }) =>
      simulationAPI.submitDecision(simulationId, { sessionId, choice }),
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

export const useCompleteSimulation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ simulationId, sessionId, survived, score }) =>
      simulationAPI.complete(simulationId, { sessionId, survived, score }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['simulations', 'history'] })
      queryClient.invalidateQueries({ queryKey: ['user', 'progress'] })
      queryClient.invalidateQueries({ queryKey: ['badges'] })

      if (data.survived) {
        toast.success(`You survived! +${data.xpGained} XP`, {
          icon: '🎉',
          duration: 5000
        })
      } else {
        toast.error('You did not survive this time. Try again!', {
          icon: '💀',
          duration: 4000
        })
      }

      if (data.newBadges && data.newBadges.length > 0) {
        data.newBadges.forEach(badge => {
          toast.success(`New Badge: ${badge.name}!`, {
            icon: '🏆',
            duration: 5000
          })
        })
      }
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

// Notification Mutations
export const useSubscribeNotifications = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ subscription, preferences }) =>
      notificationAPI.subscribe(subscription, preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      toast.success('Notifications enabled!')
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

export const useUnsubscribeNotifications = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: notificationAPI.unsubscribe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      toast.success('Notifications disabled')
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: notificationAPI.updatePreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'preferences'] })
      toast.success('Preferences updated!')
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

// Intelligence/Search Mutations
export const useSearch = () => {
  return useMutation({
    mutationFn: ({ query, userContext }) => intelligenceAPI.search(query, userContext),
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

export const useGetRecommendations = () => {
  return useMutation({
    mutationFn: intelligenceAPI.getRecommendations,
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

export const useGetEmergencyGuidance = () => {
  return useMutation({
    mutationFn: ({ situation, location, availableResources }) =>
      intelligenceAPI.getEmergencyGuidance(situation, location, availableResources),
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

// Onboarding Mutation
export const useSaveOnboarding = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: onboardingAPI.saveOnboarding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] })
      toast.success('Onboarding complete!')
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

// Family Mutations
export const useGenerateFamilyCode = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: userAPI.generateFamilyCode,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user', 'family'] })
      toast.success(`Family code: ${data.familyCode}`)
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

export const useJoinFamily = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: userAPI.joinFamily,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'family'] })
      toast.success('Joined family successfully!')
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

export const useLeaveFamily = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: userAPI.leaveFamily,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'family'] })
      toast.success('Left family')
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

// Emergency Mutations
export const useSendSOS = () => {
  return useMutation({
    mutationFn: emergencyAPI.sendSOS,
    onSuccess: () => {
      toast.success('SOS alert sent!', {
        icon: '🚨',
        duration: 5000
      })
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

export const useGetEmergencyContacts = () => {
  return useMutation({
    mutationFn: ({ country, latitude, longitude }) =>
      emergencyAPI.getContacts(country, latitude, longitude),
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

// Offline Sync Mutation
export const useSyncOfflineContent = () => {
  return useMutation({
    mutationFn: offlineAPI.sync,
    onSuccess: (data) => {
      // Store in IndexedDB or local storage
      localStorage.setItem('offline_content', JSON.stringify(data))
      localStorage.setItem('offline_sync_timestamp', Date.now().toString())
      toast.success('Content synced for offline use!')
    },
    onError: (error) => {
      toast.error(utils.handleAPIError(error))
    }
  })
}

// Export Functions (not mutations, direct calls with download)
export const useExportProgress = () => {
  const exportJSON = async () => {
    try {
      const data = await exportAPI.exportJSON()
      utils.downloadFile(data, `surviveit-progress-${Date.now()}.json`, 'application/json')
      toast.success('Progress exported as JSON!')
    } catch (error) {
      toast.error(utils.handleAPIError(error))
    }
  }

  const exportCSV = async () => {
    try {
      const csv = await exportAPI.exportCSV()
      utils.downloadFile(csv, `surviveit-progress-${Date.now()}.csv`, 'text/csv')
      toast.success('Progress exported as CSV!')
    } catch (error) {
      toast.error(utils.handleAPIError(error))
    }
  }

  const exportPDF = async () => {
    try {
      const html = await exportAPI.exportPDF()
      // Open in new window for printing to PDF
      const newWindow = window.open()
      newWindow.document.write(html)
      newWindow.document.close()
      toast.success('Progress report opened. Use Print to save as PDF.')
    } catch (error) {
      toast.error(utils.handleAPIError(error))
    }
  }

  return { exportJSON, exportCSV, exportPDF }
}
